import type { FC, ReactNode } from 'react'

import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface FloatingBlobProps {
    className?: string
    // Drift amplitude in pixels.
    amplitude?: number
    // Loop duration in seconds.
    duration?: number
    // Delay so several blobs don't move in lock-step.
    delay?: number
    // SVG path d attribute. Defaults to a soft blob shape.
    d?: string
    fill?: string
}

const DEFAULT_BLOB = 'M44.7,-67.8C57.4,-58.5,66.7,-44.6,71.6,-29.6C76.6,-14.6,77.2,1.7,72.4,16.2C67.6,30.7,57.5,43.5,44.8,53.6C32.1,63.7,16.1,71.2,-0.6,72.1C-17.3,72.9,-34.6,67.1,-47.4,56.5C-60.1,46,-68.4,30.7,-71.4,14.4C-74.4,-1.9,-72.1,-19.3,-64.2,-33C-56.3,-46.7,-42.8,-56.8,-28.5,-65.5C-14.2,-74.2,1.9,-81.6,16.7,-79.3C31.5,-77.1,45,-65.2,44.7,-67.8Z'

// A soft floating decoration. Gentle drift only, never spins or bounces.
const FloatingBlob: FC<FloatingBlobProps> = ({
    className,
    amplitude = 10,
    duration = 8,
    delay = 0,
    d = DEFAULT_BLOB,
    fill = 'currentColor'
}): ReactNode => {
    const reduce = useReducedMotion()

    return (
        <motion.svg
            aria-hidden
            viewBox='-100 -100 200 200'
            className={cn('pointer-events-none absolute select-none', className)}
            animate={
                reduce
                    ? undefined
                    : {
                        y: [0, -amplitude, 0, amplitude / 2, 0],
                        x: [0, amplitude / 3, 0, -amplitude / 4, 0]
                    }
            }
            transition={{ duration, repeat: Infinity, ease: 'easeInOut', delay }}
        >
            <path d={d} fill={fill} />
        </motion.svg>
    )
}

export default FloatingBlob
